import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { apiCall } from "../App";

const FALLBACK_QUOTES = [
  { text: "What if your relationships became your greatest spiritual accelerator instead of your heaviest karmic burden?", action: "Pause. Reflect. Choose." },
  { text: "The mirror of relationship reveals what we cannot see alone. Are you ready to look?", action: "Breathe. Accept. Transform." },
  { text: "Every trigger is a teacher. Every pattern is a pathway. Every wound is a window to wisdom.", action: "Observe. Learn. Grow." },
];

const CYCLE_MS = 10000; // 10 seconds per message

const Contemplation = () => {
  const [quotes, setQuotes] = useState(FALLBACK_QUOTES);
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    apiCall("GET", "/contemplation").then(res => {
      if (res.success && res.data.quotes?.length) setQuotes(res.data.quotes);
    });
  }, []);

  // Cycle timer
  useEffect(() => {
    const interval = setInterval(() => {
      setIdx(prev => (prev + 1) % quotes.length);
      setProgress(0);
    }, CYCLE_MS);
    return () => clearInterval(interval);
  }, [quotes.length]);

  // Progress bar
  useEffect(() => {
    const step = 50; // ms
    const inc = (step / CYCLE_MS) * 100;
    const timer = setInterval(() => {
      setProgress(prev => Math.min(prev + inc, 100));
    }, step);
    return () => clearInterval(timer);
  }, [idx]);

  const handleBegin = useCallback(() => {
    const token = localStorage.getItem("siwon_token");
    navigate(token ? "/dashboard" : "/login");
  }, [navigate]);

  const quote = quotes[idx];

  return (
    <div className="min-h-screen bg-cosmic-void flex items-center justify-center px-6" data-testid="contemplation-page">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[600px] rounded-full bg-divine-gold/5 blur-[100px]" />
      </div>

      <div className="max-w-3xl mx-auto z-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="sacred-card text-center"
          data-testid="contemplation-card"
        >
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <span className="label-sacred">CONTEMPLATION</span>
          </motion.div>

          {/* Cycling quotes with crossfade */}
          <div className="min-h-[180px] flex flex-col items-center justify-center my-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <p className="text-2xl md:text-3xl text-chakra-crown leading-relaxed mb-6 font-body" data-testid="contemplation-text">
                  {quote.text}
                </p>
                <p className="text-divine-gold italic text-lg">{quote.action}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-6" data-testid="contemplation-dots">
            {quotes.map((_, i) => (
              <button key={i} onClick={() => { setIdx(i); setProgress(0); }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${i === idx ? "bg-divine-gold w-6" : "bg-divine-gold/20 hover:bg-divine-gold/40"}`}
                data-testid={`dot-${i}`} />
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-full h-px bg-divine-gold/10 mb-8 rounded-full overflow-hidden">
            <div className="h-full bg-divine-gold/40 transition-all duration-100 ease-linear rounded-full"
              style={{ width: `${progress}%` }} data-testid="contemplation-progress" />
          </div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBegin}
            className="vedic-button inline-flex items-center gap-3"
            data-testid="begin-journey-btn"
          >
            Begin Your 3A Journey
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center text-chakra-crown/50 mt-8 text-sm tracking-wide"
        >
          Average dwell time: 30 seconds of sacred reflection
        </motion.p>
      </div>
    </div>
  );
};

export default Contemplation;
